import { apiFetch } from './http';

export interface ProductRegistryEntry {
  productId: string;
  productName: string;
  description: string;
  templatePrefix: string;
  roleRequired: string;
}

export type VersionStatus = 'draft' | 'current' | 'previous' | 'deprecated';

export interface StackVersion {
  productId: string;
  version: string;
  status: VersionStatus;
  templateS3Key: string;
  description: string;
  changelog: string;
  createdAt: string;
  createdBy: string;
  tenantsDeployed: number;
}


export async function fetchProducts(): Promise<{ products: Record<string, ProductRegistryEntry> }> {
  return apiFetch('/ops/products');
}

export async function fetchVersions(productId: string): Promise<{ productId: string; product: ProductRegistryEntry; versions: StackVersion[] }> {
  return apiFetch(`/ops/products/${productId}/versions`);
}

export async function createVersion(productId: string, input: {
  version: string; description: string; changelog: string; templateS3Key?: string;
}): Promise<StackVersion> {
  return apiFetch(`/ops/products/${productId}/versions`, {
    method: 'POST',
    body: JSON.stringify({ ...input, productId }),
  });
}

export async function updateVersion(productId: string, version: string, input: {
  status?: VersionStatus; description?: string; changelog?: string;
}): Promise<StackVersion> {
  return apiFetch(`/ops/products/${productId}/versions/${version}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function getTemplateUrl(productId: string, version: string): Promise<{ templateUrl: string }> {
  return apiFetch(`/ops/products/${productId}/versions/${version}/template-url`);
}

export interface DeployResult {
  status: string;
  action: 'create' | 'update';
  stackName: string;
  stackId?: string;
  version: string;
  templateUrl?: string;
  customerAccountId: string;
  message: string;
}

export async function deployToTenant(
  productId: string,
  version: string,
  input: { tenantId: string; customerAccountId: string; parameters?: Record<string, string> },
): Promise<DeployResult> {
  return apiFetch(`/ops/products/${productId}/versions/${version}/deploy`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface StackEvent {
  resource: string;
  type: string;
  status: string;
  reason: string;
  timestamp: string;
}

export interface CheckStatusResult {
  stackName: string;
  stackStatus: string;
  outputs: Record<string, string>;
  pcLinkStatus: string;
  isComplete: boolean;
  deployedVersion?: string | null;
  message: string;
  recentEvents?: StackEvent[];
}

export async function checkDeployStatus(
  productId: string,
  input: { tenantId: string; customerAccountId: string; stackName?: string },
): Promise<CheckStatusResult> {
  return apiFetch(`/ops/products/${productId}/versions/latest/check-status`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface PostDeployInput {
  tenantId: string;
  productId: string;
  customerAccountId: string;
  version: string;
  stackName: string;
  contactEmail: string;
  contactName?: string;
  domain?: string;
  partnerType?: string;
}

export interface PostDeployResult {
  status: string;
  outputs?: Record<string, string>;
  adminEmail?: string;
  customUrl?: string;
  pcLinkStatus?: string;
}

export async function triggerPostDeploy(
  productId: string,
  version: string,
  input: PostDeployInput,
): Promise<PostDeployResult> {
  return apiFetch(`/ops/products/${productId}/versions/${version}/post-deploy`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
